/* eslint-disable react/require-default-props */
import React, {useState} from 'react';
import {
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Modal from 'react-native-modal';
import {get} from 'lodash';

import Icon from '/component/base/Icon';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import {useMyTeamsContext} from '/component/screen/report/MyTeamsReport/hooks/useMyTeamsContext';
import {getReportName} from '/component/screen/report/MyTeamsReport/util/myTeamsAnalytics.util';
import {Reports} from '/constant/TestIDs';
import MyTeamsTestIDs from '/constant/TestIDs/MyTeams';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getSortOptions} from '/util/sort.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {styles} from './SortButtonwithArrow.style';
import {getSortByWithLabel} from './SortButtonWithArrow.util';

export interface SortButtonTeamOrgProps {
  style?: StyleProp<ViewStyle>;
  recordsName?: string;
  sortParams?: Record<string, any>;
  setSelectedSortParam?: (param: {recordsName: string; sortParam: any}) => void;
  setSelectedViewTeam?: (isViewTeam?: boolean) => void;
  disabled?: boolean;
  isViewTeamsLoading?: boolean;
  lastPaidRank?: string;
  highestAchievedRank?: string;
  coachCurrentRank?: string;
  userId?: string | number;
  isViewTeam?: boolean;
  reportMeta?: Record<string, any>;
  loadingData?: Record<string, any>;
  viewTeamsReportFilterCount?: number;
}

const SortButtonTeamOrg: React.FC<SortButtonTeamOrgProps> = ({
  style,
  recordsName = '',
  sortParams = {},
  setSelectedSortParam,
  setSelectedViewTeam,
  disabled,
  isViewTeamsLoading,
  lastPaidRank,
  highestAchievedRank,
  coachCurrentRank,
  userId,
  isViewTeam,
  reportMeta,
  loadingData,
  viewTeamsReportFilterCount,
}) => {
  const {tt} = useTranslation('report.teamOrgSortButton');
  const [modalVisible, setModalVisible] = useState(false);
  const sortLabel = get(sortParams, [recordsName, 'label'], '');
  const sortKey = get(sortParams, [recordsName, 'key'], '');
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.sortEvent,
  });

  const temorgCount = get(
    reportMeta,
    'report.total',
    get(loadingData, 'total', '0'),
  );
  const isDisabled = isViewTeam
    ? viewTeamsReportFilterCount === 0
    : temorgCount === 0;
  const isButtonDisabled =
    Boolean(disabled) || Boolean(isViewTeamsLoading) || isDisabled;

  const sortMapping: Record<string, string> = {
    firstNameAZ: 'first_name_a_z',
    firstNameZA: 'first_name_z_a',
    lastNameAZ: 'last_name_a_z',
    lastNameZA: 'last_name_z_a',
    rankAsc: 'rank_ascending',
    rankDesc: 'rank_descending',
    default: 'default',
    activationDateNew: 'activation_date_new',
    activationDateOld: 'activation_date_old',
  };
  const openModal = () => {
    setModalVisible(true);
  };
  const closeModal = () => {
    setModalVisible(false);
  };
  const {reduxActiveTab, reduxIsViewTeam} = useMyTeamsContext();
  const sortOptions = getSortOptions({recordsName});
  const sortOptionTestIDs = MyTeamsTestIDs.sortOptionTestIDs;
  const SortArray = sortOptions
    ? sortOptions.map((item: any) => {
        const sortOptionKey =
          item.key as keyof typeof MyTeamsTestIDs.sortOptionTestIDs;

        return {
          text: item.label,
          selected: item.key === get(sortParams, [recordsName, 'key'], ''),
          accessibilityLabel: `${item.label} ${
            item.key === get(sortParams, [recordsName, 'key'], '')
              ? 'selected'
              : 'not selected'
          }`,
          onPress: () => {
            const mappedValue = sortMapping[item.key];
            sendEvent({
              sortType: mappedValue,
              reportName: getReportName(reduxIsViewTeam, reduxActiveTab),
              lastPaidRank: lastPaidRank,
              coach_highest_rank: highestAchievedRank,
              coach_current_rank: coachCurrentRank,
              coach_id: userId?.toString?.() || '',
            });

            setSelectedSortParam?.({recordsName, sortParam: item});
            setSelectedViewTeam?.(isViewTeam);
            closeModal();
          },
          testID:
            sortOptionTestIDs[sortOptionKey] || `myTeams-sort-${item.key}`,
        };
      })
    : [];

  const wrapperStyle =
    style && typeof style === 'object' && 'container' in (style as any)
      ? (style as any).container
      : style;

  return (
    <View>
      <View style={wrapperStyle}>
        <TouchableOpacity
          disabled={isButtonDisabled}
          onPress={openModal}
          style={[
            styles.teamOrgsortContainer,
            isButtonDisabled && styles.disabled,
          ]}
          {...testProperties(Reports.reportSortButton, true)}>
          <Icon name="sort" style={styles.fontSize20} />
          <Text
            truncate
            style={styles.teamOrgText}
            testID={Reports.reportSortButtonLabel}>
            {tt(`sortBy-${sortKey}`, ` Sort: ${sortLabel}`)}
          </Text>
        </TouchableOpacity>
      </View>
      <Modal
        animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
        animationOut={Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'}
        style={styles.modal}
        isVisible={modalVisible}
        visible={modalVisible}
        onBackdropPress={closeModal}>
        <View style={styles.modalView}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => {
                setModalVisible(!modalVisible);
              }}
              {...testProperties(MyTeamsTestIDs.myTeamsSortCloseButton)}
              testID={MyTeamsTestIDs.myTeamsSortCloseButton}
              accessibilityLabel={MyTeamsTestIDs.myTeamsSortCloseButton}
              accessible>
              <Icon
                name="close"
                testID={MyTeamsTestIDs.myTeamsSortCloseIcon}
                accessibilityLabel={MyTeamsTestIDs.myTeamsSortCloseIcon}
                accessible
              />
            </TouchableOpacity>
            <Text
              style={styles.headerText}
              {...testProperties(Reports.reportSortByHeader)}
              testID={MyTeamsTestIDs.myTeamsSortByLabel}
              accessibilityLabel={MyTeamsTestIDs.myTeamsSortByLabel}
              isBold>
              {tt('sort', 'Sort by')}
            </Text>
          </View>
          <View
            style={styles.radioButtonsContainer}
            {...testProperties(
              MyTeamsTestIDs.myTeamsSortOptionsContainer,
              true,
            )}
            testID={MyTeamsTestIDs.myTeamsSortOptionsContainer}>
            <RadioButtonGroup
              title={getSortByWithLabel(recordsName)}
              options={SortArray}
              testID={MyTeamsTestIDs.myTeamsSortOptionsGroup}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};
export default SortButtonTeamOrg;
